import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type {
  IncorporateLessonsProposal,
  PromptVersionSource,
  SkillLesson,
  SkillPromptVersion,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

const SOURCE_LABEL: Record<PromptVersionSource, string> = {
  manual: '수동 편집',
  lesson_incorporation: 'Lesson 흡수',
  rollback: '복원',
  data_audit: '데이터 점검',
  create: '생성',
};

/**
 * 두 버전 간 변경된 추적 필드 키워드를 calc. UI 칩 노출용.
 * older 가 null (즉 이전 버전 없음 — v1 의 경우) 이면 빈 배열 반환.
 * 옛 row 와 비교 시 newer 의 필드가 null 이면 비교 스킵 (변경 없음으로 간주).
 */
function diffFields(newer: SkillPromptVersion, older: SkillPromptVersion | null): string[] {
  if (!older) return [];
  const changed: string[] = [];
  if (newer.systemPrompt !== older.systemPrompt) changed.push('prompt');
  if ((newer.name ?? null) !== (older.name ?? null) && newer.name !== null) changed.push('name');
  if ((newer.description ?? null) !== (older.description ?? null) && newer.description !== null)
    changed.push('description');
  if (newer.pairsWellWith !== null && older.pairsWellWith !== null) {
    const a = [...newer.pairsWellWith].sort().join(',');
    const b = [...older.pairsWellWith].sort().join(',');
    if (a !== b) changed.push('pairsWellWith');
  }
  if (
    newer.toolVisibility !== null &&
    older.toolVisibility !== null &&
    newer.toolVisibility !== older.toolVisibility
  )
    changed.push('visibility');
  if (newer.enabled !== null && older.enabled !== null && newer.enabled !== older.enabled)
    changed.push('enabled');
  if (newer.tools !== null && older.tools !== null) {
    const a = JSON.stringify(newer.tools);
    const b = JSON.stringify(older.tools);
    if (a !== b) changed.push('tools');
  }
  return changed;
}

interface SkillPromptEvolutionProps {
  skillId: string;
}

/**
 * Skill detail 페이지 하단에 들어가는 "프롬프트 진화" 섹션.
 *
 * - 좌측: 활성 lesson 체크박스 → "AI 초안 받기" 로 LLM 이 새 system_prompt 제안 → diff 검토 → 저장 시 lesson archive.
 * - 우측: prompt_versions 히스토리 → 클릭하면 미리보기 + "이 버전으로 복원".
 *
 * 모든 액션은 어드민 검토 게이트 (사람이 클릭) 그대로.
 */
export function SkillPromptEvolution({ skillId }: SkillPromptEvolutionProps) {
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [proposal, setProposal] = useState<IncorporateLessonsProposal | null>(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [previewVersion, setPreviewVersion] = useState<SkillPromptVersion | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lessonsQuery = useQuery({
    queryKey: ['admin', 'skill-lessons', { skillId, status: 'active' }],
    queryFn: () => api.adminSkillLessonsList({ skillId, status: 'active', limit: 100 }),
  });

  const versionsQuery = useQuery({
    queryKey: ['admin', 'skills', skillId, 'prompt-versions'],
    queryFn: () => api.adminSkillPromptVersionsList(skillId),
  });

  const proposeMut = useMutation({
    mutationFn: (lessonIds: string[]) => api.adminSkillLessonsIncorporate(skillId, { lessonIds }),
    onSuccess: (p) => {
      setProposal(p);
      setEditedPrompt(p.proposedPrompt);
      setError(null);
      setFeedback(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'AI 초안 생성 실패.'),
  });

  const commitMut = useMutation({
    mutationFn: (args: { proposedPrompt: string; lessonIds: string[] }) =>
      api.adminSkillLessonsIncorporateCommit(skillId, args),
    onSuccess: () => {
      setProposal(null);
      setEditedPrompt('');
      setSelectedIds(new Set());
      setFeedback('새 prompt 가 저장되고 lesson 들이 보관 처리되었습니다.');
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'skill-lessons'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId, 'prompt-versions'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : '저장에 실패했습니다.'),
  });

  const restoreMut = useMutation({
    mutationFn: (versionId: string) => api.adminSkillPromptVersionRestore(skillId, versionId),
    onSuccess: (v) => {
      setPreviewVersion(null);
      setFeedback(`v${v.version} 으로 복원했습니다 (새 버전 생성).`);
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'skills', skillId, 'prompt-versions'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : '복원 실패.'),
  });

  const activeLessons = lessonsQuery.data?.lessons ?? [];
  const versions = versionsQuery.data?.versions ?? [];

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  return (
    <section className="mt-8 rounded-2xl border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">프롬프트 진화</h3>
        <span className="text-xs text-foreground-muted">
          활성 lesson {activeLessons.length} · 버전 {versions.length}
        </span>
      </div>

      {feedback ? (
        <div className="mb-3 rounded-md border border-success/30 bg-success/5 px-3 py-2 text-xs text-success">
          {feedback}
        </div>
      ) : null}
      {error ? (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <IncorporateColumn
          activeLessons={activeLessons}
          selectedIds={selectedIds}
          onToggle={toggle}
          onPropose={() => {
            const ids = Array.from(selectedIds);
            if (ids.length === 0) return;
            setError(null);
            proposeMut.mutate(ids);
          }}
          proposing={proposeMut.isPending}
        />

        <VersionHistoryColumn
          versions={versions}
          loading={versionsQuery.isLoading}
          onPreview={setPreviewVersion}
          previewVersion={previewVersion}
          onRestore={(id) => {
            if (window.confirm('이 버전의 system_prompt 로 되돌립니다. 진행할까요?')) {
              restoreMut.mutate(id);
            }
          }}
          restoring={restoreMut.isPending}
        />
      </div>

      {proposal ? (
        <ProposalReview
          proposal={proposal}
          editedPrompt={editedPrompt}
          onChange={setEditedPrompt}
          onCancel={() => {
            setProposal(null);
            setEditedPrompt('');
          }}
          onCommit={() =>
            commitMut.mutate({ proposedPrompt: editedPrompt, lessonIds: proposal.lessonIds })
          }
          committing={commitMut.isPending}
        />
      ) : null}
    </section>
  );
}

function IncorporateColumn({
  activeLessons,
  selectedIds,
  onToggle,
  onPropose,
  proposing,
}: {
  activeLessons: SkillLesson[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onPropose: () => void;
  proposing: boolean;
}) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs uppercase tracking-wider text-foreground-muted">Lesson 흡수</h4>
        <Button
          variant="secondary"
          onClick={onPropose}
          disabled={proposing || selectedIds.size === 0}
        >
          {proposing ? 'AI 작성 중…' : `AI 초안 받기 (${selectedIds.size})`}
        </Button>
      </div>
      <p className="mb-2 text-xs text-foreground-muted">
        선택한 active lesson 의 의도를 system prompt 본문에 통합한 새 버전을 LLM 이 제안합니다. 저장
        시 흡수된 lesson 은 자동 보관됩니다.
      </p>
      {activeLessons.length === 0 ? (
        <p className="text-xs text-foreground-muted">활성 lesson 이 없습니다.</p>
      ) : (
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {activeLessons.map((l) => (
            <li
              key={l.id}
              className="flex items-start gap-2 rounded-sm px-2 py-1 hover:bg-surface-muted"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(l.id)}
                onChange={() => onToggle(l.id)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono text-foreground-muted">{l.toolKey}</div>
                <div className="truncate text-xs">{l.title}</div>
                <div className="line-clamp-2 text-[11px] text-foreground-muted">{l.guidance}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VersionHistoryColumn({
  versions,
  loading,
  onPreview,
  previewVersion,
  onRestore,
  restoring,
}: {
  versions: SkillPromptVersion[];
  loading: boolean;
  onPreview: (v: SkillPromptVersion | null) => void;
  previewVersion: SkillPromptVersion | null;
  onRestore: (id: string) => void;
  restoring: boolean;
}) {
  return (
    <div className="rounded-md border border-border p-3">
      <h4 className="mb-2 text-xs uppercase tracking-wider text-foreground-muted">버전 히스토리</h4>
      {loading ? (
        <p className="text-xs text-foreground-muted">불러오는 중…</p>
      ) : versions.length === 0 ? (
        <p className="text-xs text-foreground-muted">아직 버전 기록이 없습니다.</p>
      ) : (
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {versions.map((v, idx) => {
            const selected = previewVersion?.id === v.id;
            // versions 는 desc — 다음 index 가 직전 버전.
            const previous = versions[idx + 1] ?? null;
            const changed = diffFields(v, previous);
            return (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => onPreview(selected ? null : v)}
                  className={`w-full rounded-sm px-2 py-1 text-left text-xs hover:bg-surface-muted ${selected ? 'bg-surface-muted' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono">v{v.version}</span>
                    <span className="text-foreground-muted">{SOURCE_LABEL[v.source]}</span>
                  </div>
                  <div className="text-[11px] text-foreground-muted">
                    {new Date(v.createdAt).toLocaleString()}
                    {v.createdByName ? ` · ${v.createdByName}` : ''}
                  </div>
                  {changed.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {changed.map((f) => (
                        <span
                          key={f}
                          className="inline-flex rounded-sm border border-border bg-surface px-1.5 py-0.5 text-[10px] text-foreground-muted"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {v.changelog ? (
                    <div className="mt-1 truncate text-[11px] text-foreground" title={v.changelog}>
                      📝 {v.changelog}
                    </div>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {previewVersion ? (
        <div className="mt-3 border-t border-border pt-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold">v{previewVersion.version} 미리보기</span>
            <Button
              variant="secondary"
              onClick={() => onRestore(previewVersion.id)}
              disabled={restoring}
            >
              {restoring ? '복원 중…' : '이 버전으로 복원'}
            </Button>
          </div>
          {previewVersion.changelog ? (
            <div className="mb-2 rounded-md border border-border bg-surface-muted/30 px-2 py-1 text-[11px]">
              📝 {previewVersion.changelog}
            </div>
          ) : null}
          {previewVersion.name !== null ||
          previewVersion.description !== null ||
          previewVersion.pairsWellWith !== null ||
          previewVersion.toolVisibility !== null ||
          previewVersion.enabled !== null ? (
            <dl className="mb-2 grid grid-cols-[80px_1fr] gap-x-2 gap-y-1 text-[11px]">
              {previewVersion.name !== null ? (
                <>
                  <dt className="text-foreground-muted">이름</dt>
                  <dd>{previewVersion.name}</dd>
                </>
              ) : null}
              {previewVersion.description !== null ? (
                <>
                  <dt className="text-foreground-muted">설명</dt>
                  <dd>{previewVersion.description ?? '(없음)'}</dd>
                </>
              ) : null}
              {previewVersion.pairsWellWith !== null ? (
                <>
                  <dt className="text-foreground-muted">동료 skill</dt>
                  <dd>
                    {previewVersion.pairsWellWith.length > 0
                      ? previewVersion.pairsWellWith.join(', ')
                      : '(없음)'}
                  </dd>
                </>
              ) : null}
              {previewVersion.toolVisibility !== null ? (
                <>
                  <dt className="text-foreground-muted">tool 노출</dt>
                  <dd>{previewVersion.toolVisibility}</dd>
                </>
              ) : null}
              {previewVersion.enabled !== null ? (
                <>
                  <dt className="text-foreground-muted">활성</dt>
                  <dd>{previewVersion.enabled ? '예' : '아니오'}</dd>
                </>
              ) : null}
              {previewVersion.tools !== null ? (
                <>
                  <dt className="text-foreground-muted">tools</dt>
                  <dd>
                    {previewVersion.tools.length > 0
                      ? previewVersion.tools.map((t) => t.toolKey).join(', ')
                      : '(없음)'}
                  </dd>
                </>
              ) : null}
            </dl>
          ) : null}
          <pre className="max-h-64 overflow-y-auto rounded-md border border-border bg-background p-2 text-[11px] font-mono whitespace-pre-wrap">
            {previewVersion.systemPrompt}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

function ProposalReview({
  proposal,
  editedPrompt,
  onChange,
  onCancel,
  onCommit,
  committing,
}: {
  proposal: IncorporateLessonsProposal;
  editedPrompt: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onCommit: () => void;
  committing: boolean;
}) {
  return (
    <div className="mt-4 rounded-md border border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs uppercase tracking-wider text-foreground-muted">
          AI 제안 ({proposal.model}) — diff 검토
        </h4>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={committing}>
            취소
          </Button>
          <Button onClick={onCommit} disabled={committing || editedPrompt.trim().length === 0}>
            {committing ? '저장 중…' : '저장'}
          </Button>
        </div>
      </div>
      <p className="mb-2 text-xs text-foreground-muted">
        좌측은 현재 system prompt, 우측은 LLM 이 lesson 들을 통합한 제안. 우측은 직접 편집 가능.
        저장 시 흡수된 lesson 은 archive.
      </p>
      <div className="grid gap-2 md:grid-cols-2">
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wider text-foreground-muted">
            현재
          </div>
          <pre className="max-h-96 min-h-64 overflow-y-auto rounded-md border border-border bg-background p-2 text-xs font-mono whitespace-pre-wrap">
            {proposal.currentPrompt || '(비어있음)'}
          </pre>
        </div>
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wider text-foreground-muted">
            제안
          </div>
          <textarea
            value={editedPrompt}
            onChange={(e) => onChange(e.target.value)}
            rows={20}
            className="min-h-64 w-full rounded-md border border-border bg-background p-2 text-xs font-mono"
          />
        </div>
      </div>
    </div>
  );
}
